import React from "react";
import { HoverDeleteButton } from "@scripts/react/components/Button";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { MediaItemFileType } from "@/types/media";

/**
 * Shared component for rendering thumbnail content (image/spinner/icon) with container
 * Used by both FileSwitcher and FileGridPreview
 */
export function ThumbnailContent({
    thumbnail,
    isUploading,
    fileType,
    fileName,
    index,
    className,
    title,
    onClick,
    onDelete,
    isShareMode = false,
    isSelected = false,
    flowLocked = false,
    showOverlay = true,
    insideGrid = false,
    showPlayButton = false,
    isSingleFile = false
}: {
    thumbnail: string | undefined;
    isUploading: boolean;
    fileType: MediaItemFileType;
    fileName: string;
    index: number;
    className?: string;
    title?: string;
    onClick?: (evt: React.MouseEvent) => void;
    onDelete?: (evt: React.MouseEvent) => void;
    isShareMode?: boolean;
    isSelected?: boolean;
    flowLocked?: boolean;
    showOverlay?: boolean;
    insideGrid?: boolean;
    showPlayButton?: boolean;
    isSingleFile?: boolean;
}) {
    const imageClassName = "h-full w-full rounded-md object-cover";
    const spinnerClassName = "fa fa-spinner fa-spin text-sm text-gray-500";
    const iconClassName = `fa ${fileType === "video" ? "fa-spinner fa-spin" : "fa-image"} text-sm text-gray-500`;

    const selectedClasses = twClassNames(
        flowLocked && "cursor-not-allowed opacity-70 ring-1",
        isSelected && "ring-2",
        !isSelected && !flowLocked && "cursor-pointer ring-1 hover:ring-2",
        isShareMode && {
            "h-12 w-12": true,
            "ring-gray-500": flowLocked,
            "ring-purboflow-300": isSelected,
            "ring-gray-500 hover:ring-gray-300": !isSelected && !flowLocked
        },
        !isShareMode && {
            "insideGrid": insideGrid,
            "ring-gray-300": flowLocked,
            "ring-brand": isSelected,
            "ring-gray-300 hover:ring-gray-400": !isSelected && !flowLocked
        }
    );
    let content;
    if (thumbnail) {
        content = <img src={thumbnail} alt={fileName} className={imageClassName} />;
    } else {
        const icon = isUploading ? spinnerClassName : iconClassName;
        content = (
            <div className="flex flex-col items-center justify-center gap-2">
                <i className={icon} />
                {isSingleFile && (
                    <span className="animate-pulse text-xs text-tertiary">Uploading...</span>
                )}
            </div>
        );
    }

    return (
        <div
            className={twClassNames("fileThumbnail group", selectedClasses, className)}
            onClick={onClick}
            title={title}
            aria-label={title || fileName}
        >
            {content}
            {!isUploading && onDelete && (
                <HoverDeleteButton
                    onClick={onDelete}
                    ariaLabel={`Delete ${fileName}`}
                    className="-right-1 -top-1 z-10"
                />
            )}
            {showOverlay && (
                <div className={twClassNames("thumbnailOverlay", isSelected && "selected")} />
            )}
            {showPlayButton && fileType === "video" && thumbnail && !isUploading && (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-black bg-opacity-60 backdrop-blur-sm transition-all duration-200 group-hover:scale-110 group-hover:bg-opacity-80">
                        <i className="fa fa-play ml-1 text-2xl text-white" />
                    </div>
                </div>
            )}
        </div>
    );
}
